import './EasySoftwareInputEditor.scss'

import React, { useEffect, useRef, useState } from 'react'

import { usePuxEditor } from '../PuxEditor/PuxEditorContext'
import type { SaveResult } from '../PuxEditor/PuxEditorState'

export interface EasySoftwareInputEditorType {
  fieldId: string
  wysiwygId?: string
  className?: string
  inline?: boolean
  small?: boolean
  path: string
  pageContentItemId: string
  saveEditor: (value: string) => Promise<SaveResult>
}

export default function EasySoftwareInputEditor(
  props: EasySoftwareInputEditorType
) {
  const { store, session } = usePuxEditor()
  const input = useRef<HTMLInputElement>(null)
  const original = useRef(``)
  const [value, setValue] = useState(``)
  const save = useRef(props.saveEditor)
  save.current = props.saveEditor

  useEffect(
    () =>
      store.register(props.fieldId, {
        save: () => save.current(input.current?.value.trim() ?? ``),
      }),
    [store, props.fieldId]
  )

  useEffect(() => {
    const controller = new AbortController()
    async function load() {
      try {
        const response = await fetch(
          `${
            process.env.GATSBY_ORCHARD_API_URL
          }/puxapi/contentItem/getField?contentItemId=${encodeURIComponent(
            props.pageContentItemId
          )}&path=${encodeURIComponent(props.path)}`,
          { credentials: `include`, signal: controller.signal }
        )
        if (!response.ok) throw new Error(`Load failed`)
        const content = await response.text()
        if (controller.signal.aborted) return
        original.current = content
        setValue(content)
        store.patch(props.fieldId, { loading: false, error: null })
      } catch {
        if (!controller.signal.aborted)
          store.patch(props.fieldId, {
            error: `Unable to load content. Cancel and reopen to retry.`,
          })
      }
    }
    void load()
    return () => controller.abort()
  }, [store, props.fieldId, props.pageContentItemId, props.path])

  useEffect(() => {
    if (!session?.loading) input.current?.focus()
  }, [session?.loading])

  const classes = [
    `InputEditor`,
    props.inline ? `InputEditor--inline` : `InputEditor--spaced`,
    props.small ? `InputEditor--small` : ``,
    props.className,
  ]
  return (
    <div className={classes.filter(Boolean).join(` `)}>
      <input
        ref={input}
        id={`wysiwyg-${props.wysiwygId}`}
        className='Input'
        aria-label={session?.label ?? `Edit text`}
        disabled={session?.loading || session?.saving}
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
          store.patch(props.fieldId, {
            dirty: event.target.value !== original.current,
          })
        }}
      />
    </div>
  )
}
